'use client';

import type { ReactNode } from "react";
import { useCallback, useEffect } from "react";
import { Button } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import AutoScroll from "embla-carousel-auto-scroll";
import useEmblaCarousel from "embla-carousel-react";
import clsx from "clsx";
import type { MediaRef } from "@/app/features/shared/__to_be_integrated/types/general/media/media";
import MediaView from "@/app/features/shared/media-view/ui/public/MediaView.public";
import styles from "./ImageCarousel.module.scss";

interface ImageCarouselBaseProps {
    images: MediaRef[];
    autoScroll?: boolean;
    loop?: boolean;
    leadingSlide?: ReactNode;
    renderImageActions?: (image: MediaRef, index: number) => ReactNode;
    openModalGallery?: boolean;
}

export default function ImageCarouselBase({
    images,
    autoScroll = false,
    loop = false,
    leadingSlide,
    renderImageActions,
    openModalGallery = false,
}: ImageCarouselBaseProps) {
    const [emblaRef, emblaApi] = useEmblaCarousel(
        { loop },
        autoScroll
            ? [AutoScroll({
                speed: 1,
                stopOnFocusIn: false,
                stopOnInteraction: true,
            })]
            : [],
    );

    useEffect(() => {
        if (!loop) emblaApi?.scrollTo(0);
    }, [emblaApi, loop]);

    const scrollPrevious = useCallback(() => {
        emblaApi?.plugins().autoScroll?.stop();
        emblaApi?.scrollPrev();
    }, [emblaApi]);

    const scrollNext = useCallback(() => {
        emblaApi?.plugins().autoScroll?.stop();
        emblaApi?.scrollNext();
    }, [emblaApi]);

    return (
        <div className="w-full max-w-[90svw]">
            <div className="flex items-stretch gap-2">
                <Button
                    variant="outlined"
                    aria-label="Vorheriges Bild"
                    className={clsx(styles.navButton, styles.navButtonLeft)}
                    onClick={scrollPrevious}
                >
                    <ChevronLeft />
                </Button>
                <div className="relative overflow-hidden flex-1" ref={emblaRef}>
                    <div className="flex">
                        {leadingSlide}
                        {images.map((image, index) => (
                            <div key={index} className="relative flex-none w-[300px] h-[300px] px-2">
                                <MediaView
                                    src={image}
                                    relatedImages={images}
                                    width={300}
                                    height={300}
                                    slots={{
                                        root: { sx: { width: "auto" } },
                                        image: {
                                            style: {
                                                height: "300px",
                                                objectFit: "cover",
                                            },
                                        },
                                    }}
                                    modalViewType={openModalGallery ? "click" : "none"}
                                    metadataOverlayOptions={{
                                        showName: true,
                                        variant: "titleOnly",
                                    }}
                                />
                                {renderImageActions?.(image, index)}
                            </div>
                        ))}
                    </div>
                </div>
                <Button
                    variant="outlined"
                    aria-label="Nächstes Bild"
                    className={clsx(styles.navButton, styles.navButtonRight)}
                    onClick={scrollNext}
                >
                    <ChevronRight />
                </Button>
            </div>
        </div>
    );
}
